import React from "react";
import { ArrowLeftIcon, ArrowUpRightIcon, CheckIcon } from "lucide-react";
import { categories } from "../../data/categories";
import { colors } from "../../data/colors";
import { products } from "../../data/products";
import { Highlight } from "../Highlight";
import { ProductPreview } from "../design/ProductPreview";
import { StepHeading } from "../StepHeading";
import { formatEuro, getLowestPrice } from "../../utils/pricing";
import type { Configurator } from "../../hooks/useConfigurator";
interface ProductStepProps {
  config: Configurator;
}
export function ProductStep({
  config
}: ProductStepProps) {
  const {
    category,
    state,
    selectCategory,
    clearCategory,
    selectProduct
  } = config;
  if (!category) {
    return <section data-id="element-127" data-mp="38v11demy6v9z">
        <StepHeading title={<>
              Que voulez-vous <Highlight data-id="element-129" data-mp="3dvyjjr7yjpbh">personnaliser</Highlight> ?
            </>} subtitle="Choisissez un objet ou un vêtement, puis composez-le en direct avec votre logo, vos textes et vos couleurs." data-id="element-128" data-mp="38xn5t5ejkunz" />
        <ul className="grid grid-cols-2 border-l border-t border-line sm:grid-cols-3 lg:grid-cols-4" data-id="element-130" data-mp="2vhvjc57miep7">
          {categories.map(c => {
          const Icon = c.icon;
          const count = products.filter(p => p.categoryId === c.id).length;
          return <li key={c.id} className="border-b border-r border-line" data-id="element-131" data-mp="2gklaz4qa1oxp">
                <button type="button" onClick={() => selectCategory(c.id)} className={`group flex h-full w-full flex-col items-start justify-between gap-8 p-5 text-left transition-colors duration-150 ease-out md:p-6 ${c.accent === "mint" ? "hover:bg-mint" : "hover:bg-rose"}`} data-id="element-132" data-mp="3e7lbekk04mdf">
                  <div className="flex w-full items-start justify-between" data-id="element-133" data-mp="rvt8d4g3vjz">
                    <Icon size={44} strokeWidth={1.25} aria-hidden="true" data-id="element-134" data-mp="u06hlo8i6wx" />
                    <ArrowUpRightIcon size={20} className="opacity-0 transition-opacity duration-150 ease-out group-hover:opacity-100" aria-hidden="true" data-id="element-135" data-mp="3s5ibvs1ktcxd" />
                  </div>
                  <div data-id="element-136" data-mp="1gk9exws7meap">
                    <p className="text-base font-bold leading-tight md:text-lg" data-id="element-137" data-mp="1giybjy2of8k4">{c.name}</p>
                    <p className="mt-1 text-sm text-muted group-hover:text-ink" data-id="element-138" data-mp="1lh9l07p27h1t">{count} modèles</p>
                  </div>
                </button>
              </li>;
        })}
        </ul>
        <p className="mt-6 text-sm text-muted" data-id="element-139" data-mp="1bl68q90hu215">Prix indicatifs hors frais de marquage et transport.</p>
      </section>;
  }
  const list = products.filter(p => p.categoryId === category.id);
  return <section data-id="element-140" data-mp="12k8cicwaqyt4">
      <button type="button" onClick={clearCategory} className="mb-6 inline-flex items-center gap-2 text-sm font-bold underline-offset-4 hover:underline" data-id="element-141" data-mp="12lsy3d0mv7eu">
        <ArrowLeftIcon size={16} aria-hidden="true" data-id="element-142" data-mp="2wwy9j3hyoo4q" />
        Toutes les catégories
      </button>
      <StepHeading title={<>
            <Highlight tone={category.accent} data-id="element-144" data-mp="gbt877vzjw1z">{category.name}</Highlight>
          </>} subtitle="Sélectionnez le modèle à personnaliser. Couleur, logo, textes et technique se composent à l'étape suivante, avec aperçu en direct." data-id="element-143" data-mp="law5g65q4xze" />
      <ul className="grid grid-cols-1 border-l border-t border-line sm:grid-cols-2 xl:grid-cols-3" data-id="element-145" data-mp="q862a6vg0bx9">
        {list.map(p => {
        const selected = state.productId === p.id;
        const firstHex = colors.find(c => c.id === p.colors[0])?.hex ?? "#FFFFFF";
        return <li key={p.id} className="border-b border-r border-line" data-id="element-146" data-mp="1dyjld7w1v0fp">
              <button type="button" onClick={() => selectProduct(p)} aria-pressed={selected} className={`relative flex h-full w-full flex-col text-left outline-none transition-colors duration-150 ease-out focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ink ${selected ? "bg-ink text-paper" : "hover:bg-mist"}`} data-id="element-147" data-mp="18zozfx2scvrd">
                <ProductPreview mockupId={p.mockup} hex={firstHex} className="w-full border-b border-line" data-id="element-148" data-mp="26vkmawizlicr" />
                <span className="absolute left-4 top-4 flex gap-1" aria-label={`${p.colors.length} coloris`} data-id="element-149" data-mp="2bqq9dyo4l32f">
                  {p.colors.slice(0, 6).map(cid => <span key={cid} className="h-3 w-3 rounded-full border border-ink/30" style={{
                backgroundColor: colors.find(c => c.id === cid)?.hex
              }} data-id="element-150" data-mp="1j5gk8tlg5qnv" />)}
                </span>
                {selected && <span className="absolute right-4 top-4 flex h-8 w-8 items-center justify-center rounded-full bg-mint text-ink" data-id="element-151" data-mp="1wxeoor47qydp">
                    <CheckIcon size={18} strokeWidth={3} aria-hidden="true" data-id="element-152" data-mp="14er7vnw925if" />
                  </span>}
                <div className="flex flex-1 flex-col p-5" data-id="element-153" data-mp="wvqbrnns3e53">
                  <p className={`text-xs ${selected ? "text-paper/70" : "text-muted"}`} data-id="element-154" data-mp="wt40pe1oigi4">{p.ref}</p>
                  <p className="mt-1 text-lg font-bold leading-tight" data-id="element-155" data-mp="rukd797fswnn">{p.name}</p>
                  <p className={`mt-2 text-sm ${selected ? "text-paper/80" : "text-muted"}`} data-id="element-156" data-mp="nwwhw51u11v8">{p.description}</p>
                  <div className="mt-auto flex items-end justify-between gap-4 pt-6" data-id="element-157" data-mp="iy1ns6yezqvt">
                    <p className="text-sm" data-id="element-158" data-mp="dyp2vrr3stvv">
                      dès <span className="text-lg font-bold tabular-nums" data-id="element-159" data-mp="95nkh9rouc7j">{formatEuro(getLowestPrice(p))}</span> HT
                    </p>
                    <p className={`text-xs ${selected ? "text-paper/70" : "text-muted"}`} data-id="element-160" data-mp="47ceja4iklul">min. {p.minQty} pcs</p>
                  </div>
                </div>
              </button>
            </li>;
      })}
      </ul>
    </section>;
}